import { useDispatch, useSelector } from 'react-redux';
import { useToggleAttendanceMutation, useGetTodayStatusQuery } from '../features/attendance/attendanceApi';
import {
    selectIsClockedIn,
    selectTodaySummary,
    selectFormattedTotalHours,
    selectSlots,
    selectActiveSlot,
    selectNextSlot,
    selectAllShiftsDone
} from '../features/attendance/attendanceSlice';
import { toast } from 'react-hot-toast';
import { getCurrentPosition } from '../utils/geolocation';

/**
 * Custom hook to share attendance logic across components (Header, Dashboard, etc.)
 */
export const useAttendance = () => {
    const dispatch = useDispatch();
    const isClockedIn = useSelector(selectIsClockedIn);
    const todaySummary = useSelector(selectTodaySummary);
    const formattedTotalHours = useSelector(selectFormattedTotalHours);
    const slots = useSelector(selectSlots);
    const activeSlot = useSelector(selectActiveSlot);
    const nextSlot = useSelector(selectNextSlot);
    const allShiftsDone = useSelector(selectAllShiftsDone);

    const [toggle, { isLoading: isToggling }] = useToggleAttendanceMutation();
    const { isFetching, refetch } = useGetTodayStatusQuery();

    const handleToggle = async () => {
        if (allShiftsDone) {
            toast.error("All of today's scheduled shifts have already been completed.");
            return;
        }
        try {
            // Best-effort — if the branch has no geofencing configured the backend
            // ignores these fields entirely, so it's safe to always attempt this.
            const { position } = await getCurrentPosition();

            const res = await toggle({
                deviceInfo: navigator.userAgent,
                sessionId: crypto.randomUUID(),
                lat: position?.lat,
                lng: position?.lng,
                accuracy: position?.accuracy,
            }).unwrap();

            if (res.success) {
                const newStatus = !!(res.data.first_in && !res.data.last_out);
                const shiftLabel = res.data.slot === 2 ? ' (Shift 2)' : '';
                toast.success((newStatus ? 'Shift Started' : 'Shift Ended') + shiftLabel, {
                    icon: newStatus ? '🌅' : '🌙',
                });
            }
        } catch (err: any) {
            const apiError = err.data?.error;
            const message = typeof apiError === 'string' ? apiError : apiError?.message;
            toast.error(message || 'Failed to update shift status');
        }
    };

    return {
        isClockedIn,
        todaySummary,
        formattedTotalHours,
        slots,
        activeSlot,
        nextSlot,
        allShiftsDone,
        handleToggle,
        isLoading: isToggling || isFetching,
        refresh: refetch
    };
};
